import { useTranslation } from 'react-i18next';

import { formatMoney, formatQtyWithUnit } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * GEN-97, GEN-98, GEN-99 (and GEN-30/GEN-54's column halves, GEN-90/
 * GEN-177's pinning, and
 * N-9/N-10): every money figure shows both decimals, in the monospace figure
 * font, pinned left-to-right — enforced here as components rather than as a
 * habit. GEN-91b rides the same formatter: Western digits, comma groups, dot
 * decimals, identical in both languages.
 *
 * Every money figure on screen: monospace tabular digits in an LTR island, so
 * Arabic text around it mirrors while the figure itself never does (PART 4
 * §4.10). <bdi> keeps the isolation without fighting the surrounding
 * bidi context.
 *
 * NFR-03 is the rule these two components exist to keep — "every number,
 * amount, date, time, code, email address, and telephone number must be pinned
 * left to right, in both languages". It is written as one property of two
 * shared components rather than as a habit, because a habit is not checkable —
 * and `quantity-decimals.test.tsx` asserts the COMPOSED island ("+12.500 kg"
 * as one string, not three elements), which is the assertion that would fail
 * if the pinning were dropped and the parts left loose in the paragraph.
 */
export function Amount({
  value,
  currency,
  className,
  decimals,
}: {
  value: string;
  currency?: string | undefined;
  className?: string | undefined;
  /**
   * BR-DSH-10-15's summary-card half — "whole currency units" — and the only
   * caller that passes it is `SummaryCards`. Everything else takes the default
   * 2, which is what the same rule requires "inside detail tables" and what
   * every document screen has always shown.
   */
  decimals?: 0 | 2 | undefined;
}) {
  return (
    <bdi dir="ltr" className={cn('font-mono tabular-nums', className)}>
      {formatMoney(value, currency, decimals ?? 2)}
    </bdi>
  );
}

/** Dates and codes are LTR islands too (FIN-03 §15, NFR-03). */
export function Ltr({ value, className }: { value: string; className?: string | undefined }) {
  return (
    <bdi dir="ltr" className={className}>
      {value}
    </bdi>
  );
}

/**
 * `N-4` on screen: "Quantities must be shown with the number of decimals the
 * item's unit of measure allows, and ALWAYS with the unit written next to
 * them."
 *
 * ── THE UNIT IS ON THE ROW, NOT IN THE COLUMN HEADER ───────────────────────
 * `N-14` decides it: "items are counted in pieces, kilograms, litres, metres
 * and boxes", so the rows of one stock table do not share a unit and a header
 * can only be true of some of them. It then says outright that "a quantity on
 * a row must ALWAYS be shown with its unit, and never added to the row above"
 * — and a bare column of numbers is exactly what invites the addition it
 * forbids. So the symbol repeats. That is the cost of the rule, and it is the
 * cheaper half of it.
 *
 * ── THE BIDI PROBLEM, AND WHY IT IS ONE ISLAND ─────────────────────────────
 * `4.000 kg` in Arabic is a number, a space, and a symbol that may itself be
 * Arabic script. Rendered as loose text inside an RTL paragraph the bidi
 * algorithm is free to reorder the run, and the unit can end up on the wrong
 * side of its own figure — a quantity that reads as a different quantity. So
 * the pair is composed FIRST and rendered as a single `<bdi dir="ltr">`,
 * exactly as `<Amount>` does for `-$46,866.91` (PART 4 §4.10: "figures, codes
 * and dates stay left-to-right"). Wrapping the number alone and leaving the
 * symbol outside would put the boundary in the one place it must not be.
 *
 * `precision` is `units.precision` for the item's STOCK unit. It is required,
 * with no default, because a default is how this rule went three citations
 * without an implementation.
 */
export function Qty({
  value,
  precision,
  unit,
  className,
}: {
  value: string;
  precision: number;
  unit: string;
  className?: string | undefined;
}) {
  return (
    <bdi dir="ltr" className={cn('font-mono tabular-nums', className)}>
      {formatQtyWithUnit(value, precision, unit)}
    </bdi>
  );
}

/**
 * NFR-01's LAST HAND-WRITTEN UNITS — `<Hours>`, `<Days>`, `<Pct>`.
 *
 * The rule is "no text may be written into a screen", and its audit named the
 * survivors: the letter `h` after an hour count, the letter `d` after a day
 * count, the `%` sign, and the ▲/▼ glyphs in `ChangePill`. Each was a unit
 * attached to a number BY KEYBOARD at the call site — which no dictionary can
 * translate and no test can see.
 *
 * `<Qty>` above is the precedent and these follow it exactly: the value and
 * its unit are composed ONCE, inside one `<bdi dir="ltr">` island, and the
 * unit text comes from the DICTIONARY (`unit.hours` = "{value}h" / "{value}س",
 * `unit.days`, `unit.percent`), so Arabic decides its own convention and the
 * i18n parity test sees the key like any other. The island matters for the
 * same reason it does on `<Qty>`: the Arabic hour letter is RTL script, and
 * loose in an RTL paragraph the bidi algorithm may put it on the wrong side of
 * its own figure.
 *
 * WHEN A RUN OF FIGURES SHARES ONE ISLAND — "12h / 40h", "3 / 8h · 62%" — the
 * call site keeps its single `<Ltr>` island and composes with
 * `t('unit.hours', { value })` inside it, because splitting the run into
 * several islands puts neutral separators (`/`, `·`) between isolates, and in
 * an RTL paragraph the paragraph direction decides their order. Same
 * dictionary text, same one-island rule.
 *
 * The ▲/▼ pair moved to the dictionary the same way (`unit.up`, `unit.down`):
 * both languages ship the same glyph today, but which glyph means "rose" is
 * now the translation's decision, not the keyboard's.
 */
export function Hours({ value, className }: { value: string | number; className?: string | undefined }) {
  const { t } = useTranslation();
  return (
    <bdi dir="ltr" className={className}>
      {t('unit.hours', { value })}
    </bdi>
  );
}

export function Days({ value, className }: { value: string | number; className?: string | undefined }) {
  const { t } = useTranslation();
  return (
    <bdi dir="ltr" className={className}>
      {t('unit.days', { value })}
    </bdi>
  );
}

export function Pct({ value, className }: { value: string | number; className?: string | undefined }) {
  const { t } = useTranslation();
  return (
    <bdi dir="ltr" className={className}>
      {t('unit.percent', { value })}
    </bdi>
  );
}

/**
 * `N-4`'s COUNTDOWN — the read-only quantity displays still printing a figure
 * with neither its unit's decimals nor its unit, and what each one needs.
 *
 * Written down rather than left implicit, because "quantities are formatted
 * wrong" is a CLASS, and this build has now been bitten three times by a class
 * nobody could check. Every entry needs the same two things on the wire —
 * `unitSymbol` and `unitPrecision` — after which the screen is a `<Qty>`
 * substitution. THE LIST MAY ONLY SHRINK: a screen that has been converted
 * never comes back onto it.
 *
 * A screen NEWLY FOUND is a different thing and belongs here. PART 9's entry
 * was three screens and is now two DIFFERENT ones: converting the recipe, the
 * job and the ledger is what put eyes on the planning board and the job
 * register, which nobody had listed and which have the same gap. Leaving them
 * out to keep the count falling would be the countdown lying about the work.
 *
 * ── IT IS AT ZERO, AND IT STAYS ────────────────────────────────────────────
 *
 * MFG-03's planning board and MFG-02's job register were the last two, and
 * `production-planning.ts` now carries `unitSymbol`/`unitPrecision` on all four
 * of its paged rows. The list is EMPTY, not deleted: it is the register that
 * makes "quantities are formatted wrong" checkable, and a register is only
 * worth anything while it is still there to be added to. The next screen that
 * prints a bare quantity gets a line here before it gets a fix.
 *
 * WHERE THE SYMBOL GOES WHEN A ROW HOLDS SEVERAL FIGURES, since that is the
 * decision every one of these conversions actually turned on: ONCE per row, on
 * the figure the row is about, and never on each of a run that shares a unit. A
 * net-requirement row is one subtraction of one item's stock, so `287 pcs`
 * lands on §5.6's "answer" column and the three columns feeding it carry
 * decimals only; a job's `12 / 40` is one measurement of one thing, so the
 * symbol sits on the target cell beside it and not inside the fraction. The
 * rule this serves is `N-14`'s — the unit belongs to the ROW — and the trap it
 * avoids is printing a symbol an adjacent cell already shows.
 *
 * NOT on it, and deliberately:
 *
 *  - **Input values.** `trimZeros` prefilling an EDITABLE box (the adjustment,
 *    transfer, receipt, delivery, return and count editors) is right: `N-4`
 *    governs how a quantity is SHOWN, and making a person delete "0.000"
 *    before they can type is not what the rule asks for. What those screens
 *    DRAW obeys it; what they hold mid-edit does not.
 *
 *    THE LINE IS "EDITABLE", NOT "IS AN `<INPUT>`", and that distinction is
 *    where most of this countdown actually lived. A posted voucher, a
 *    completed transfer, a confirmed delivery note or GRN, a posted credit or
 *    debit note — each kept its quantity in a box that was merely `disabled`,
 *    and a greyed-out box holding "40" is a display wearing an input's
 *    clothes. Those documents are locked BY RULE (`BR-INV-08-13`,
 *    `BR-SAL-03-07`, R8): the box can never be typed into again, so it is no
 *    longer holding anything mid-edit and it renders as `<Qty>`.
 *  - **Sums across items.** INV-06's net card and anything like it have no
 *    single unit to name, and `N-14` says so outright. Those print bare, with
 *    the reason at the call site. CHECKED AND REJECTED, not overlooked: the
 *    transfer register's total-quantity column, the transfer editor's footer,
 *    INV-07's in-transit banner, and the DN and GRN footers — one document can
 *    carry 40.5 kg of sheet and 12 boxes of screws, so a unit on any of them
 *    would be a claim about a sum `N-14` calls meaningless.
 *  - **A quantity inside a sentence the spec writes out.** MFG-02 §6 gives the
 *    button its label in full — `Mark Complete (Remaining 0)` — and MFG-03 §5.7
 *    gives the empty band its words. A unit spliced into a quoted sentence is
 *    no longer the sentence the spec asked for, and `N-4` governs a quantity
 *    DISPLAY rather than the prose around it. CHECKED AND REJECTED, not
 *    overlooked.
 */
export const QTY_WITHOUT_UNIT: readonly { screen: string; what: string }[] = [] as const;
